import { useState, useEffect } from 'react';
import apiService, { getStorageUrl } from '../../../../services/apiService';
import { useTenant } from '../../../../contexts/TenantContext';

const ThrowballDownloads = () => {
  const { siteSettings } = useTenant();
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/public/downloads');

      if (response.data.success && response.data.data) {
        setDownloads(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching downloads:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return '#';
    return getStorageUrl(filePath) || '#';
  };

  return (
    <section className="tb-page-section tb-downloads-section">
      <div className="tb-container">
        {/* Top Banner */}
        <div className="tb-page-banner">
          <img src={siteSettings?.page_banners?.downloads || '/theme/assets/images/banner-4.jpg'} alt="Downloads Banner" />
        </div>

        {/* Page Content */}
        <div className="tb-page-content">
          <div className="tb-heading-top">
            <h1 className="tb-page-title">DOWNLOADS</h1>
            <span className="tb-mini-line"></span>
          </div>

          {loading ? (
            <div className="downloads-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading downloads...</p>
            </div>
          ) : downloads.length === 0 ? (
            <div className="downloads-empty">
              <i className="fas fa-download"></i>
              <p>No downloads available at the moment.</p>
            </div>
          ) : (
            <div className="downloads-list">
              {downloads.map((download, index) => (
                <a
                  key={download.id || index}
                  href={getFileUrl(download.file_path)}
                  download
                  className="download-item"
                >
                  <div className="download-icon">
                    <i className="fas fa-file-pdf"></i>
                  </div>
                  <div className="download-info">
                    <span className="download-title">{download.title || download.file_name}</span>
                    {download.file_size && (
                      <span className="download-size">({formatFileSize(download.file_size)})</span>
                    )}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .tb-downloads-section .tb-page-content {
          padding: 2rem 1rem;
        }

        .downloads-loading,
        .downloads-empty {
          text-align: center;
          padding: 60px 20px;
        }

        .downloads-loading i,
        .downloads-empty i {
          font-size: 3rem;
          color: #c00f15;
          margin-bottom: 16px;
          display: block;
        }

        .downloads-loading p,
        .downloads-empty p {
          color: #666;
          font-size: 1.1rem;
        }

        .downloads-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 800px;
          margin: 0 auto;
        }

        .download-item {
          display: flex;
          align-items: center;
          padding: 20px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          text-decoration: none !important;
          transition: all 0.2s ease;
          gap: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .download-item * {
          text-decoration: none !important;
        }

        .download-item:hover {
          border-color: #c00f15;
          box-shadow: 0 4px 12px rgba(192, 15, 21, 0.1);
          transform: translateY(-2px);
        }

        .download-icon {
          width: 50px;
          height: 50px;
          background: #fef2f2;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .download-icon i {
          font-size: 24px;
          color: #dc3545;
        }

        .download-info {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .download-title {
          color: #1f2937;
          font-size: 1rem;
          font-weight: 500;
          text-decoration: none;
        }

        .download-item:hover .download-title {
          text-decoration: none;
        }

        .download-size {
          color: #9ca3af;
          font-size: 0.9rem;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .download-item {
            padding: 16px;
            gap: 12px;
          }

          .download-icon {
            width: 40px;
            height: 40px;
          }

          .download-icon i {
            font-size: 20px;
          }

          .download-title {
            font-size: 0.95rem;
          }
        }

        @media (max-width: 480px) {
          .download-item {
            padding: 14px;
          }

          .download-title {
            font-size: 0.9rem;
          }

          .download-size {
            font-size: 0.8rem;
          }
        }
      `}</style>
    </section>
  );
};

export default ThrowballDownloads;
